/**
 * Construcción del HTML del modal de la radio.
 *
 * Migrado desde la función `buildHTML()` de web/legacy/radio.js.
 *
 * Estructura del modal:
 *   #r-overlay         (fondo oscuro a pantalla completa)
 *     #r-modal         (caja del modal, grid 3 columnas: 288px / 1fr / 420px)
 *       #r-hdr         (cabecera con título y botones EDITAR/CERRAR)
 *       #r-explorer    (panel izquierdo: PLAYLISTS / CANCIONES / CARPETA)
 *       #r-player      (panel central: imagen del boombox + overlays)
 *       #r-library     (panel derecho: pila de cassettes + cover activa)
 *       #r-newpl-modal (modal interno para crear nueva playlist)
 *
 * La función NO inserta el modal en el DOM ni le añade listeners. Solo
 * construye y devuelve el `<div>` raíz. Eso es responsabilidad de mount.ts.
 */

/** Construye el modal completo. Devuelve el elemento raíz `#r-overlay`. */
export function buildModal(): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.id = 'r-overlay';
  overlay.innerHTML = MODAL_HTML;
  return overlay as HTMLDivElement;
}

/**
 * HTML del modal como template literal.
 *
 * Mantenido como string porque:
 *  - No hay variables a interpolar (los renderers rellenan zonas vacías
 *    como `#r-pl-list`, `#r-stack`, etc. con su propio innerHTML después).
 *  - Es legible como bloque y fácil de comparar con el legacy.
 *  - Para los pocos sitios con datos dinámicos (covers picker), construimos
 *    el DOM con createElement en otros archivos.
 */
const MODAL_HTML = `
<div id="r-modal" role="dialog" aria-label="Radio Logbook System" aria-modal="true">

  <div id="r-hdr">
    <div class="r-hdr-dot"></div>
    <span>📻 RADIO LOGBOOK SYSTEM</span>
    <span style="opacity:.35">—</span>
    <span style="opacity:.55">51 ESCUADRÓN</span>
    <button id="r-edit-btn" title="Modo edición de hotspots (E)" hidden>[E] EDITAR</button>
    <button id="r-close-btn">[ESC] CERRAR</button>
  </div>

  <!-- IZQ: marco r_Filebrowser.png con 3 secciones: PLAYLISTS, CANCIONES, CARPETA -->
  <div id="r-explorer">
    <div class="r-frame">
      <div class="r-fx-screen">

        <div class="r-pl-section">
          <div class="r-section-hdr">
            <span class="r-tri">▼</span> PLAYLISTS
            <div class="r-actions">
              <button id="r-new-pl-btn">+ NUEVA</button>
            </div>
          </div>
          <div class="r-pl-list" id="r-pl-list"></div>
        </div>

        <div class="r-tracks-section">
          <div class="r-section-hdr">
            <span class="r-tri">▼</span> CANCIONES
            <div class="r-actions">
              <button id="r-save-btn" title="Guardar canciones de esta playlist">SAVE</button>
            </div>
          </div>
          <div class="r-fx-list" id="r-fx-list"></div>
        </div>

        <div class="r-folder-section">
          <div class="r-section-hdr">
            <span class="r-tri">▼</span> CARPETA
            <div class="r-actions">
              <button id="r-pick-folder">+ CARPETA</button>
            </div>
          </div>
          <div class="r-fd-list" id="r-fd-list"></div>
        </div>

      </div>
    </div>
    <input id="r-folder-input" type="file" multiple
           accept="audio/*,.mp3,.wav,.ogg,.m4a,.flac"
           webkitdirectory directory style="display:none">
  </div>

  <!-- CENTRO: reproductor con assets reales -->
  <div id="r-player">
    <div class="r-radio-wrap">
      <img class="r-radio-img" decoding="async" src="/sprites/radio_player.png" alt="51 Escuadrón Boombox">

      <div class="r-led" id="r-led"></div>
      <div class="r-led-play" id="r-led-play"></div>
      <div class="r-reel" id="r-reel-l"></div>
      <div class="r-reel r-fast" id="r-reel-r"></div>
      <div class="r-knob" id="r-knob"
           title="Rueda del ratón o arrastrar verticalmente"></div>

      <div class="r-hs" id="r-hs-rec"   title="REC (deshabilitado)">
        <img decoding="async" src="/sprites/r_Rec.png" alt="REC">
      </div>
      <div class="r-hs" id="r-hs-play"  title="Play / Pausa">
        <img decoding="async" src="/sprites/r_Play.png" alt="PLAY">
      </div>
      <div class="r-hs" id="r-hs-rew"   title="Rebobinar (mantén pulsado, STOP para detener)">
        <img decoding="async" src="/sprites/r_Rew.png" alt="REW">
      </div>
      <div class="r-hs" id="r-hs-ff"    title="Avance rápido (mantén pulsado, STOP para detener)">
        <img decoding="async" src="/sprites/r_FF.png" alt="FF">
      </div>
      <div class="r-hs" id="r-hs-stop"  title="Detener">
        <img decoding="async" src="/sprites/r_Stop.png" alt="STOP">
      </div>
      <div class="r-hs" id="r-hs-pause" title="Pausa">
        <img decoding="async" src="/sprites/r_Pause.png" alt="PAUSE">
      </div>

      <div class="r-prog-row">
        <span id="r-tc">0:00</span>
        <div class="r-prog-bar" id="r-prog">
          <div class="r-prog-fill" id="r-prog-fill"></div>
        </div>
        <span id="r-tt">0:00</span>
      </div>
    </div>

    <div class="r-edit-banner" id="r-edit-banner" style="display:none">
      <span>✎ MODO EDICIÓN — Arrastra para mover · esquina ↘ para redimensionar</span>
      <button id="r-edit-reset" class="r-warn">RESET</button>
      <button id="r-edit-done">HECHO</button>
    </div>
  </div>

  <!-- DER: pila de cassettes inactivas + cover de la activa -->
  <div id="r-library">
    <div class="r-pnl-hdr">
      <span>📼 ACTIVE PLAYLIST</span>
    </div>
    <div class="r-lib-body">
      <div class="r-stack" id="r-stack"></div>
      <div class="r-cover-big" id="r-cover-big">
        <div class="r-cover-wrap">
          <img id="r-cover-img" class="r-cover-img" src="" alt="">
          <span id="r-cover-spine" class="r-cover-spine"></span>
        </div>
      </div>
    </div>
    <div class="r-active-tag" id="r-atag">● ACTIVE PLAYLIST</div>
  </div>

  <!-- Modal interno: nueva playlist -->
  <div id="r-newpl-modal">
    <div class="r-newpl-box">
      <h3>✦ NUEVA PLAYLIST ✦</h3>
      <label>NOMBRE DE LA PLAYLIST</label>
      <input type="text" id="r-newpl-name" maxlength="40" placeholder="MI PLAYLIST">
      <label>CARÁTULA</label>
      <div class="r-cover-picker" id="r-cover-picker"></div>
      <div class="r-newpl-actions">
        <button class="r-cancel" id="r-newpl-cancel">CANCELAR</button>
        <button id="r-newpl-create">CREAR</button>
      </div>
    </div>
  </div>

</div>`;
